/**
 * Integration with the Northbeam marketing platform (https://northbeam.io). Instead of building a custom
 * analytics.js plugin, our integration hooks into analytics.js event emitters to track purchases in Northbeam.
 */
import analytics, { isTrackedMarketingProduct, shouldTrackMarketingEvent } from '.';
import { getTenancy } from '../../hooks/useTenancy';
import { NorthbeamProduct, NorthbeamPurchaseEventProperties } from '../../types/NorthbeamAnalytics';
import { getProductsAndVariantsBySku } from '../product';
import store from '../reduxStore';
import { typedWindow } from '../Window';
import { ECOM_EVENT_ORDER_COMPLETED, SegmentProduct, SegmentPurchaseEventProperties } from './events';

function northbeamPixel() {
  // In production, use the Northbeam Pixel SDK, if available.
  const tenancy = getTenancy();
  if (tenancy !== 'test' && process.env.REACT_APP_ENVIRONMENT === 'production') {
    return (
      typedWindow.Northbeam || {
        firePurchaseEvent: () => {
          // Pixel is blocked in some browsers so it won't exist on the window object. Do nothing.
        },
      }
    );
  }

  // Development environment. Log for debugging.
  return {
    firePurchaseEvent: (properties: NorthbeamPurchaseEventProperties) => {
      console.debug('Northbeam purchase event', properties);
    },
  };
}

/**
 * Map Segment e-commerce cart event properties (multiple products) to Facebook Pixel event properties.
 * We only include products that are S3 collar kits or S3 subscriptions.
 */
function northbeamPurchaseEventProperties(properties: SegmentPurchaseEventProperties) {
  if (!shouldTrackMarketingEvent(properties)) {
    return;
  }

  const productsAndVariantsBySku = getProductsAndVariantsBySku(store.getState().config.products);
  const segmentProducts = properties.products || [];

  // Our marketing team only cares about tracked collar kits. Filter products to only include tracked collar kits
  // and their associated subscriptions that aren't upgrades.
  const filteredProducts: SegmentProduct[] = segmentProducts.filter((segmentProduct: SegmentProduct) =>
    isTrackedMarketingProduct(segmentProduct, productsAndVariantsBySku),
  );

  if (!filteredProducts.length) {
    return;
  }

  const northbeamProducts: NorthbeamProduct[] = [];

  const taxPrice = properties.tax ?? 0;
  const shippingPrice = properties.shipping ?? 0;

  // We'll manually calculate our total price to use the analytics price for the subscription.
  let totalPrice = taxPrice + shippingPrice;

  // The Northbeam backend API documentation seems to indicate that productId should be unique so we'll keep
  // things consistent on the frontend and aggregate the products by sku and sum the quantity.
  for (const product of filteredProducts) {
    const price = product.analyticsSubscriptionValue ?? product.subscriptionPrice ?? 0;
    totalPrice += price * product.quantity;

    const existingProduct = northbeamProducts.find((p) => p.productId === product.sku && p.price === price);
    if (existingProduct) {
      existingProduct.quantity += product.quantity;
      continue;
    }

    northbeamProducts.push({
      productId: product.sku,
      variantId: product.sku,
      productName: product.name,
      variantName: product.variant ?? '',
      price,
      quantity: product.quantity,
    });
  }

  return {
    id: properties.orderId,
    totalPrice,
    shippingPrice: shippingPrice,
    taxPrice: taxPrice,
    coupons: properties.code ?? '',
    customerId: analytics.userOrAnonymousId(),
    currency: properties.currency,
    lineItems: northbeamProducts,
  };
}

/**
 * We only need to track purchases for the Northbeam pixel and we only care about S3 collar purchases.
 */
function onTrack(event: string, properties: SegmentPurchaseEventProperties) {
  if (event !== ECOM_EVENT_ORDER_COMPLETED) {
    return;
  }

  // If the order doesn't contain products we care about, the won't be any properties.
  const northbeamProperties = northbeamPurchaseEventProperties(properties);
  if (!northbeamProperties?.lineItems.length) {
    return;
  }

  northbeamPixel().firePurchaseEvent(northbeamProperties);
}

export function northbeamInit() {
  analytics.onTrack(onTrack);
}
